<script setup>
import { PrimaryButton, SecondaryButton } from "@Components/Button";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { ref } from "vue";
import { QueueListIcon as QueueListIconOutline } from "@heroicons/vue/24/outline";
import Tooltip from "@Components/Tooltip.vue";
import { ArrowDownTrayIcon } from "@heroicons/vue/20/solid";
import Logs from "@Components/Logs/Logs.vue";
import { useI18n } from "vue-i18n";
import { format } from "date-fns";
import { Modal } from "@Components/Modal";
import { useForm } from "@/form";
import { MagicIcon } from "@Icons/index";

const props = defineProps({
    generation: {
        type: Object,
        required: true,
    },
});

const { t: __ } = useI18n();

const logsModalOpen = ref(false);

const downloadLogs = () => {
    let lines = [];

    props.generation.logs.forEach((log) => {
        lines.push(format(new Date(log.created_at), "yyyy-MM-dd HH:mm:ss") + " - " + log.message);
    });

    const url = window.URL.createObjectURL(
        new Blob([lines.join("\n")], {
            type: "text/plain;charset=utf-8",
        })
    );
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = "debug.log";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
};

const analyseLogsProcessing = ref(false);
const analyseLogsModalRef = ref();
const analyseLogsResponse = ref(null);

const analyseLogs = async () => {
    if (analyseLogsProcessing.value) {
        return;
    }

    analyseLogsProcessing.value = true;

    analyseLogsResponse.value = null;

    const response = await useForm({
        logs: props.generation.logs,
        language: props.generation.language.key,
    }).post("analyse_logs");

    analyseLogsResponse.value = response;
    logsModalOpen.value = false;
    analyseLogsModalRef.value.show();

    analyseLogsProcessing.value = false;
};
</script>
<template>
    <div v-if="generation.logs.length > 0">
        <PrimaryButton @click="logsModalOpen = true">{{ __("Afficher les logs") }}</PrimaryButton>

        <TransitionRoot as="template" :show="logsModalOpen">
            <Dialog as="div" class="z-[100000] relative" @close="logsModalOpen = false">
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0">
                    <div class="bg-opacity-75 fixed inset-0 bg-gray-900 transition-opacity" />
                </TransitionChild>

                <div class="z-[100000] overflow-y-auto fixed inset-0">
                    <div class="min-h-full flex justify-center items-end p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild
                            as="template"
                            enter="ease-out duration-300"
                            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enter-to="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leave-from="opacity-100 translate-y-0 sm:scale-100"
                            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel
                                class="transform w-full overflow-hidden relative px-4 pt-5 pb-4 text-left bg-gray-800 rounded-lg border border-gray-700 shadow-xl transition-all lg:max-w-3xl sm:max-w-lg sm:p-6 sm:my-8">
                                <div class="sm:flex sm:items-start">
                                    <div
                                        class="flex-shrink-0 mx-auto w-12 h-12 flex justify-center items-center bg-gray-700 rounded-full sm:w-10 sm:h-10 sm:mx-0">
                                        <QueueListIconOutline class="w-6 h-6 text-gray-200" aria-hidden="true" />
                                    </div>
                                    <div class="flex-grow mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <div class="flex justify-between items-center space-x-3">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-white">Logs</DialogTitle>
                                            <Tooltip :content="__('Télécharger')">
                                                <button
                                                    @click="downloadLogs()"
                                                    class="flex items-center text-gray-400 rounded-full focus-visible:outline-2 focus-visible:outline-gray-200 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-200">
                                                    <ArrowDownTrayIcon class="w-5 h-5" aria-hidden="true" />
                                                </button>
                                            </Tooltip>
                                        </div>
                                        <Logs class="mt-3" :logs="generation.logs" />

                                        <div v-if="generation.status === 'failed'" class="flex justify-center mt-6">
                                            <SecondaryButton @click="analyseLogs" :icon="MagicIcon" :processing="analyseLogsProcessing" :z="3">
                                                {{ __("Comprendre les erreurs") }}
                                            </SecondaryButton>
                                        </div>
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </TransitionRoot>

        <Modal ref="analyseLogsModalRef">
            <div class="p-6 whitespace-break-spaces dark:text-white">
                {{ analyseLogsResponse }}
            </div>
        </Modal>
    </div>
</template>
